import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Box, Button, ButtonGroup, ClickAwayListener, IconButton, Paper, Popper, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useNavigate } from 'react-router-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ko } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import './adminStatsDatepicker.css';
import AdminTabs from '../../components/Admin/AdminTabs';
import Header from '../../components/common/Header';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import { getAdminStatisticsApi } from '../../api';

registerLocale('ko', ko);

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function formatDateYYYYMMDD(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}.${m}.${d}.`;
}

function formatDateForMode(date, mode) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  if (mode === 'monthly') return `${y}.${m}.`;
  return formatDateYYYYMMDD(date);
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function startOfMonth(date) {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function VisitorsChart({ labels = [], values = [] }) {
  const width = 390;
  const height = 260;
  const padL = 35;
  const padR = 15;
  const padT = 20;
  const padB = 44;

  const v = useMemo(() => (Array.isArray(values) ? values : []), [values]);
  const maxV = v.length ? Math.max(...v) : 0;
  const minV = 0;
  const yMax = Math.max(2, maxV);
  const span = yMax - minV || 1;

  const points = useMemo(() => {
    return v.map((val, idx) => {
      const x = padL + (idx * (width - padL - padR)) / Math.max(v.length - 1, 1);
      const norm = (val - minV) / span;
      const y = padT + (1 - norm) * (height - padT - padB);
      return { x, y, val };
    });
  }, [v, span]);

  const linePoints = points.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPoints =
    points.length > 0
      ? `${padL},${height - padB} ${linePoints} ${width - padR},${height - padB}`
      : '';

  const yTicks = useMemo(() => {
    const ticks = yMax <= 3 ? [0, 1, 2] : [0, Math.round(yMax / 2), yMax];
    return ticks;
  }, [yMax]);

  return (
    <Box
      sx={{
        bgcolor: 'transparent',
        overflow: 'visible',
      }}
    >
      <Box sx={{ width: '100%', overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* horizontal grid */}
          {yTicks.map((t) => {
            const norm = (t - minV) / span;
            const y = padT + (1 - norm) * (height - padT - padB);
            return (
              <g key={t}>
                <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#dbeafe" strokeDasharray="4 4" />
                <text x={padL - 10} y={y + 4} textAnchor="end" fontSize="12" fill="#64748b">
                  {t}
                </text>
              </g>
            );
          })}

          {/* x axis */}
          <line x1={padL} y1={height - padB} x2={width - padR} y2={height - padB} stroke="#cbd5e1" />

          {/* area */}
          {areaPoints && (
            <polyline
              points={areaPoints}
              fill="#f4fbff"
              stroke="none"
            />
          )}

          {/* line */}
          <polyline fill="none" stroke="#2563eb" strokeWidth="3" points={linePoints} />

          {/* points */}
          {points.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#2563eb" stroke="white" strokeWidth="2" />
          ))}

          {/* x labels */}
          {labels.slice(0, points.length).map((l, idx) => {
            const p = points[idx];
            return (
              <text
                key={idx}
                x={p?.x ?? 0}
                y={height - 18}
                textAnchor="middle"
                fontSize="12"
                fill="#64748b"
              >
                {l}
              </text>
            );
          })}
        </svg>
      </Box>
    </Box>
  );
}

function StatCard({ title, value }) {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid #e5e7eb',
        bgcolor: 'white',
        boxShadow: '0 6px 16px rgba(0,0,0,0.10)',
        px: 2,
        py: 2,
        minHeight: 92,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: 14 }}>{title}</Typography>
      <Typography sx={{ fontWeight: 800, color: '#111827', fontSize: 20, lineHeight: 1 }}>{value}</Typography>
    </Box>
  );
}

const AdminStats = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState('daily'); 
  const [selectedDailyDate, setSelectedDailyDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [selectedMonthDate, setSelectedMonthDate] = useState(() => startOfMonth(new Date()));
  const activeDate = mode === 'monthly' ? selectedMonthDate : selectedDailyDate;

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openMonthPicker, setOpenMonthPicker] = useState(false);
  const [tempDailyDate, setTempDailyDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [tempMonthDate, setTempMonthDate] = useState(() => startOfMonth(new Date()));
  const [dailyPickerKey, setDailyPickerKey] = useState(0);
  const [monthPickerKey, setMonthPickerKey] = useState(0);

  const dateAnchorRef = useRef(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const formatYMD = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate()
    ).padStart(2, '0')}`;
  };

  const formatYM = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const fetchStats = useCallback(
    async (baseDate) => {
      const dateParam = formatYMD(baseDate);
      const monthParam = formatYM(baseDate);
      try {
        setError('');
        const res = await getAdminStatisticsApi({ date: dateParam, month: monthParam });
        const payload = res?.data || res;
        setStats(payload);
      } catch (e) {
        setError(e?.message || '통계를 불러오지 못했습니다.');
      }
    },
    []
  );

  useEffect(() => {
    fetchStats(activeDate);
  }, [activeDate, fetchStats]);

  useEffect(() => {
    if (openDatePicker) setTempDailyDate(selectedDailyDate);
  }, [openDatePicker, selectedDailyDate]);

  useEffect(() => {
    if (openMonthPicker) setTempMonthDate(selectedMonthDate);
  }, [openMonthPicker, selectedMonthDate]);

  const formatNumber = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return '-';
    return n.toLocaleString('ko-KR');
  };

  const chart = useMemo(() => {
    const series = mode === 'daily' ? stats?.dailyVisitors : stats?.monthlyVisitors;
    return {
      labels: series?.labels || [],
      values: (series?.values || []).map((x) => clamp(Number(x), 0, 999999)),
    };
  }, [mode, stats]);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const closeAllPickers = () => {
    setOpenDatePicker(false);
    setOpenMonthPicker(false);
  };

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          pb: 2,
          position: 'relative',
        }}
      >
        <MainMenuDrawer open={menuOpen} onClose={() => setMenuOpen(false)} onNavigate={handleNavigate} />
        <Header variant="full" onClickMenu={() => setMenuOpen(true)} />

        <AdminTabs />

        <Box sx={{ mt: 2 }}>
          {error && (
            <Typography sx={{ fontWeight: 600, color: '#d32f2f', fontSize: 14 }}>{error}</Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <StatCard title="누적 총 방문자수" value={`${formatNumber(stats?.totalVisitors)}명`} />
            <StatCard title="누적 총 회원수" value={`${formatNumber(stats?.totalMembers)}명`} />
            <StatCard title="활성 회원수" value={`${formatNumber(stats?.activeMembers)}명`} />
            <StatCard title="일 신규 회원수" value={`${formatNumber(stats?.dailyNewMembers)}명`} />
            <StatCard title="일 방문자수" value={`${formatNumber(stats?.todayVisitors)}명`} />
            <StatCard title="월 방문자수" value={`${formatNumber(stats?.monthVisitors)}명`} />
          </Box>

          <Box sx={{ mt: 3 }}>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1,
                mb: 1,
              }}
            >
              {/* 1행: 타이틀 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>
                  방문자수
                </Typography>
              </Box>

              {/* 2행: 날짜/토글 */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {/* 가운데: 날짜 컨트롤 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, gridColumn: 2 }}>
                  <IconButton
                    size="small"
                    sx={{ color: '#000', p: 0.25 }}
                    onClick={() => {
                      if (mode === 'monthly') {
                        setSelectedMonthDate((d) => {
                          const next = startOfMonth(addMonths(d, -1));
                          return next > today ? startOfMonth(today) : next;
                        });
                      } else {
                        setSelectedDailyDate((d) => {
                          const next = addDays(d, -1);
                          return next > today ? today : next;
                        });
                      }
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                    {formatDateForMode(activeDate, mode)}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ color: '#000', p: 0.25 }}
                    ref={dateAnchorRef}
                    onClick={() => {
                      if (mode === 'monthly') {
                        setOpenMonthPicker(true);
                        setOpenDatePicker(false);
                      } else {
                        setOpenDatePicker(true);
                        setOpenMonthPicker(false);
                      }
                    }}
                  >
                    <CalendarMonthOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: '#000', p: 0.25 }}
                    disabled={
                      mode === 'monthly'
                        ? startOfMonth(addMonths(activeDate, 1)) > today
                        : addDays(activeDate, 1) > today
                    }
                    onClick={() => {
                      if (mode === 'monthly') {
                        setSelectedMonthDate((d) => {
                          const next = startOfMonth(addMonths(d, 1));
                          return next > today ? startOfMonth(today) : next;
                        });
                      } else {
                        setSelectedDailyDate((d) => {
                          const next = addDays(d, 1);
                          return next > today ? today : next;
                        });
                      }
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* react-datepicker (일별) */}
                <Popper open={openDatePicker} anchorEl={dateAnchorRef.current} placement="bottom" sx={{ zIndex: 2000 }}>
                  <ClickAwayListener onClickAway={() => setOpenDatePicker(false)}>
                    <Paper sx={{ p: 0.75, borderRadius: 2, boxShadow: '0 12px 30px rgba(0,0,0,0.18)' }}>
                      <DatePicker
                        key={`admin-stats-daily-${dailyPickerKey}`}
                        inline
                        locale="ko"
                        selected={tempDailyDate}
                        openToDate={tempDailyDate}
                        maxDate={today}
                        calendarClassName="admin-stats-datepicker"
                        onChange={(date) => {
                          if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
                          const d = new Date(date);
                          d.setHours(0, 0, 0, 0);
                          if (d > today) return;
                          setTempDailyDate(d);
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                        <Button
                          size="small"
                          sx={{ minWidth: 64, px: 1.25, height: 32, fontSize: 13 }}
                          onClick={() => {
                            const d = new Date(today);
                            d.setHours(0, 0, 0, 0);
                            setTempDailyDate(d);
                            setDailyPickerKey((k) => k + 1);
                          }}
                        >
                          오늘
                        </Button>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            sx={{ minWidth: 64, px: 1.25, height: 32, fontSize: 13 }}
                            onClick={() => setOpenDatePicker(false)}
                          >
                            취소
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ minWidth: 64, px: 1.25, height: 32, fontSize: 13 }}
                            onClick={() => {
                              setSelectedDailyDate(tempDailyDate);
                              setOpenDatePicker(false);
                            }}
                          >
                            확인
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </ClickAwayListener>
                </Popper>

                {/* react-datepicker (월별) */}
                <Popper
                  open={openMonthPicker}
                  anchorEl={dateAnchorRef.current}
                  placement="bottom"
                  sx={{ zIndex: 2000 }}
                >
                  <ClickAwayListener onClickAway={() => setOpenMonthPicker(false)}>
                    <Paper sx={{ p: 0.75, borderRadius: 2, boxShadow: '0 12px 30px rgba(0,0,0,0.18)' }}>
                      <DatePicker
                        key={`admin-stats-month-${monthPickerKey}`}
                        inline
                        locale="ko"
                        selected={tempMonthDate}
                        openToDate={tempMonthDate}
                        maxDate={today}
                        showMonthYearPicker
                        calendarClassName="admin-stats-datepicker"
                        onChange={(date) => {
                          if (!(date instanceof Date) || Number.isNaN(date.getTime())) return;
                          const m = startOfMonth(date);
                          if (m > today) return;
                          setTempMonthDate(m);
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                        <Button
                          size="small"
                          sx={{ minWidth: 64, px: 1.25, height: 32, fontSize: 13 }}
                          onClick={() => {
                            setTempMonthDate(startOfMonth(new Date(today)));
                            setMonthPickerKey((k) => k + 1);
                          }}
                        >
                          오늘
                        </Button>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            sx={{ minWidth: 64, px: 1.25, height: 32, fontSize: 13 }}
                            onClick={() => setOpenMonthPicker(false)}
                          >
                            취소
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ minWidth: 64, px: 1.25, height: 32, fontSize: 13 }}
                            onClick={() => {
                              setSelectedMonthDate(tempMonthDate);
                              setOpenMonthPicker(false);
                            }}
                          >
                            확인
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </ClickAwayListener>
                </Popper>

                {/* 오른쪽: 토글 */}
                <ButtonGroup variant="outlined" size="small" sx={{ justifySelf: 'end', gridColumn: 3 }}>
                  <Button
                    onClick={() => {
                      setMode('daily');
                      closeAllPickers();
                    }}
                    sx={{
                      px: 1,
                      borderColor: mode === 'daily' ? '#c9e0ff' : '#e5e7eb',
                      bgcolor: mode === 'daily' ? '#f4f8ff' : 'white',
                      color: mode === 'daily' ? '#0d47a1' : '#111827',
                      '&:hover': { bgcolor: mode === 'daily' ? '#f4f8ff' : '#f8fafc' },
                    }}
                  >
                    일별
                  </Button>
                  <Button
                    onClick={() => {
                      setMode('monthly');
                      closeAllPickers();
                    }}
                    sx={{
                      px: 1,
                      borderColor: mode === 'monthly' ? '#c9e0ff' : '#e5e7eb',
                      bgcolor: mode === 'monthly' ? '#f4f8ff' : 'white',
                      color: mode === 'monthly' ? '#0d47a1' : '#111827',
                      '&:hover': { bgcolor: mode === 'monthly' ? '#f4f8ff' : '#f8fafc' },
                    }}
                  >
                    월별
                  </Button>
                </ButtonGroup>
              </Box>
            </Box>

            {/* 그래프 */}
            <VisitorsChart labels={chart.labels} values={chart.values.map((x) => clamp(x, 0, 999999))} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminStats;


